import type { TFunction } from "i18next";
import {
  selectLeaderboardItems,
  type TournamentLeaderboardItem,
} from "@/hooks/tournaments/use-tournament-leaderboard";
import type { Tournament } from "./types";
import { tournamentLabels } from "./labels";

import { timeLeftRing } from "@/igw-button/ring";
import { selectTournamentProgressByCampaignId } from "@/stores/tournament-progress.store";
import { campaignKey } from "../../campaign-key";
import { optInRows, registerCampaignType } from "../../registry";
import { canJoinCampaign } from "../../can-join";
import { formatButtonPrizeTitle, formatCompactScore } from "@/utils/helpers";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";
import {
  getTournamentScoreColumnLabel,
  isHighestWinStyleTournament,
} from "@/utils/tournament-score-label";
import type {
  CampaignTypeModule,
  CurrentCampaign,
  WheelStreamData,
} from "../../registry";
import type { AppStore } from "@/stores/app.store";
import { selectTournamentById } from "@/stores/tournament.store";
import { selectJoined } from "@/stores/campaigns.store";
import {
  isActiveCampaignStatus,
  isFinishedCampaignStatus,
} from "../../campaign-status";

import type { CampaignView } from "@/components/campaigns/campaign-shell/campaign-view";
import { FINISHED_DIM } from "@/components/campaigns/campaign-shell/campaign-view";
import {
  EligibleGamesSection,
  HowToWinCard,
  InfoSection,
  CampaignTimeLeftChip,
} from "@/components/campaigns/campaign-shell/campaign-sections";
import { TournamentLeaderboardTable } from "@/components/campaigns/tournament/tournament-leaderboard-table";
import { TournamentBoard } from "@/components/campaigns/tournament/tournament-board";
import { HowToGetPointsCard } from "@/components/campaigns/tournament/how-to-get-points-card";
import { YourStandingCard } from "@/components/campaigns/tournament/your-standing-card";
import { TournamentDescription } from "@/components/campaigns/tournament/tournament-description";
import { TournamentPrizeBoard } from "@/components/campaigns/tournament/tournament-prize-board";
import {
  CampaignJoinCta,
  CampaignJoinStickyBar,
} from "@/components/campaign-card/campaign-join-cta";
import { AtLg, BelowLg } from "@/components/breakpoint";
import { CampaignFinishedCard } from "@/components/campaign-card/campaign-finished-card";
import { TournamentHomeCard } from "@/components/campaigns/tournament/tournament-home-card";
import { TournamentDockPanel } from "@/components/campaigns/tournament/tournament-dock-panel";
import { FinishedTournamentCard } from "@/components/campaign-card/finished-campaign/finished-tournament-card";
import { cn } from "@/lib/utils";

/** A side cell's or a rolling row's points (D-139), formatted as your own
 *  cell's: the number with a highest-win "x" in it, else "pts" after it
 *  (plural by the score); past a million the compact form your cell drops
 *  to ("123.5M"), so a nine-digit neighbour never out-precisions you or
 *  runs into the plate's fade (👺 Belmont, B3). One formatter for the
 *  resting cells and the stream (B5). */
function sideScoreLabel(
  n: number,
  subtype: Parameters<typeof isHighestWinStyleTournament>[0],
  t: TFunction
): { score: string; unit?: string } {
  const highestWin = isHighestWinStyleTournament(subtype);
  const figure = n >= 1_000_000 ? formatCompactScore(n) : n.toLocaleString();
  return {
    score: `${figure}${highestWin ? "x" : ""}`,
    unit: highestWin ? undefined : t("tournament.pointsUnit", { count: n }),
  };
}

const TOURNAMENT_HOW_TO_WIN =
  "Place qualifying bets to climb the leaderboard — the higher you rank, the bigger your prize.";

const tournamentData = (current: CurrentCampaign): Tournament =>
  current.data as Tournament;

function detailView(
  current: CurrentCampaign,
  t: TFunction
): CampaignView | null {
  if (current.type !== "tournament") return null;
  const campaign = current.campaign;
  const tournament = tournamentData(current);
  const campaignId = campaign.id;
  const joined = current.joined;
  const finished = campaign.status === "FI";
  const canJoin = canJoinCampaign(campaign, joined);
  // Point tournaments expose scoring tasks ("how to get points"); highest-coin ones
  // don't, so fall back to the generic how-to-win copy there.
  const tasks = tournament.tasks;
  const minimumBet = tournament.minimum_bet;
  const finishedTitle = tournamentLabels(tournament, t).finished;
  const howToPlay =
    tasks.length > 0 ? (
      <HowToGetPointsCard
        tasks={tasks}
        minimumBet={minimumBet}
        maxSpins={tournament.campaign.max_spins}
      />
    ) : (
      <HowToWinCard
        text={t("tournament.howToWinText", TOURNAMENT_HOW_TO_WIN)}
        minimumBet={minimumBet}
      />
    );
  return {
    campaign,
    primaryLabel: t("tournament.leaderboard", "Leaderboard"),
    finished,
    // Sideways phones carry it in the standing column instead (`landscape.narrow`), so it
    // doesn't stretch across both panes.
    mobileNotice: finished ? (
      <CampaignFinishedCard
        campaign={campaign}
        title={finishedTitle}
        className="ls:hidden"
      />
    ) : undefined,
    sidebar: finished ? (
      // Finished: the finished card takes the CTA slot on desktop (mobile
      // shows it under the tabs via mobileNotice); how-to-win stays but
      // greyed out (Figma 5086:1740); the final standing keeps full colour.
      <>
        <AtLg>
          <CampaignFinishedCard campaign={campaign} title={finishedTitle} />
        </AtLg>
        <AtLg>
          <div className={FINISHED_DIM}>{howToPlay}</div>
        </AtLg>
        {joined && (
          <AtLg>
            <YourStandingCard campaignId={campaignId} />
          </AtLg>
        )}
      </>
    ) : (
      <>
        {/* Not joined: the desktop join CTA sits at the top of the sidebar
            (mobile uses the sticky bar instead — see mobileStickyAction). */}
        {canJoin && (
          <AtLg>
            <CampaignJoinCta campaign={campaign} />
          </AtLg>
        )}
        <AtLg>{howToPlay}</AtLg>
        {joined && (
          <AtLg>
            <YourStandingCard campaignId={campaignId} />
          </AtLg>
        )}
      </>
    ),
    guide: finished ? (
      <div className={FINISHED_DIM}>{howToPlay}</div>
    ) : (
      howToPlay
    ),
    primary: (
      // Desktop: the whole leaderboard inline-scrollable (~10 rows) with a
      // pinned "You" row. Phones: the whole board inline in the Overview (TournamentBoard).
      // Mounted per breakpoint, not both hidden: the full table is the heaviest thing on the
      // page (every row's prize text and medal), and phones never show it.
      <>
        <AtLg>
          <TournamentLeaderboardTable campaignId={campaignId} />
        </AtLg>
        <BelowLg>
          <TournamentBoard campaignId={campaignId} joined={joined} />
        </BelowLg>
      </>
    ),
    // Sideways: the player's standing on the left (it reads "—" with a "place a qualifying bet"
    // line before they have played, so the column is never empty), the board on the right
    // scrolling inside its own pane. How points are earned lives in the Info tab, as on portrait
    // phones (the `guide` slot leads it).
    landscape: {
      narrowSide: "start",
      narrow: (
        <>
          {finished && (
            <CampaignFinishedCard campaign={campaign} title={finishedTitle} />
          )}
          <YourStandingCard campaignId={campaignId} />
        </>
      ),
      wide: <TournamentBoard campaignId={campaignId} joined={false} />,
    },
    info: (
      <div className="flex w-full flex-col gap-2 lg:gap-4">
        <InfoSection
          title={t("tournament.prizesHeading", "Prizes")}
          className={cn(finished && FINISHED_DIM)}
        >
          <TournamentPrizeBoard prizes={tournament.prizes} />
        </InfoSection>
        <InfoSection
          title={t("campaign.aboutHeading", "About")}
          aside={<CampaignTimeLeftChip campaign={campaign} />}
        >
          <TournamentDescription />
        </InfoSection>
        <EligibleGamesSection
          games={tournament.eligible_games}
          count={tournament.eligible_games_count}
          campaignId={tournament.campaign.id}
        />
      </div>
    ),
    canJoin,
    mobileStickyAction: canJoin ? (
      <CampaignJoinStickyBar campaign={campaign} />
    ) : undefined,
  };
}

/** Tournaments (`campaign.type` "TO" or "RA" — see `./assemble`). */
/** The grand prize — prizes are place-ranged, so the lowest `place_from` is 1st. */
const topPrize = (tournament: Tournament) =>
  [...tournament.prizes].sort(
    (a, b) => (a.place_from ?? 0) - (b.place_from ?? 0)
  )[0];

/** The nearest paying place better than `place` (the closest one above you
 *  on the board, across gaps between prize ranges), or null when none. */
const nearestPaidAbove = (tournament: Tournament, place: number) => {
  let best: number | null = null;
  for (const p of tournament.prizes) {
    const from = p.place_from ?? 1;
    const to = p.place_to ?? from;
    if (from >= place) continue;
    const closest = Math.min(to, place - 1);
    if (best === null || closest > best) best = closest;
  }
  return best;
};

/** The prize whose place range holds `place`, if any. */
const prizeAt = (tournament: Tournament, place: number) =>
  tournament.prizes.find(
    (p) =>
      (p.place_from ?? 1) <= place && place <= (p.place_to ?? p.place_from ?? 0)
  );

const tournamentModule: CampaignTypeModule = {
  id: "tournament",
  providers: true,
  launcher: {
    // The coin drains the time left; the final hour is urgent.
    ring: (campaign, now) => timeLeftRing(campaign, now),
    // An ended tournament keeps its place for its display window, in its
    // disabled variant (`heroDisabled`).
    showsFinished: true,
    // Whether you opted in arrives with the progress stream: until then the
    // launcher shows the coin pending.
    pending: (current, state) =>
      !(campaignKey(current.campaign) in state.joinedById),
    // The hero is your cell on the leaderboard wheel, or the nudge to play.
    rows: (current, state, t) => {
      // Ended (still in its display window): the disabled variant — your
      // frozen cell under the "Finished" pill, or "Finished" alone without
      // a standing (the pill's material); no ask. It waits pending like a
      // live one until the join state is known (no "Finished" flash first).
      const ended = isFinishedCampaignStatus(current.campaign.status);
      const progress = selectTournamentProgressByCampaignId(
        state,
        campaignKey(current.campaign)
      );
      const place = progress?.leaderboard_position ?? null;
      const score = progress?.score ?? null;
      const prize = topPrize(current.data as Tournament);
      const prizeRows = prize && {
        top: formatButtonPrizeTitle(prize.title, t),
        topKind: "prize" as const,
        topLabel: t("campaign.topPrize"),
        prizeCatalog: prize.is_catalog_item,
      };
      // The join state arrives with the progress stream: the launcher holds
      // the coin pending meanwhile (`pending`); if the stream never comes,
      // it shows only what is true either way — the prize, no ask (a joined
      // player must never be told to opt in; the panel's prompt has the same
      // guard).
      // Finished: the dark "Finished" chip on top and, on the dark chip
      // under it, your result — the prize you won in gold with a gold
      // check after it, or "No win" (Sebastian, 2026-10-19: "a faded cell
      // at top saying finished, and the main cell shows if you won or no
      // win"; "a dark faded cell, the gold prize with a gold checkmark
      // after, remove the position"). Not joined, or no standing known: the
      // cell reads "Finished" alone.
      if (ended) {
        if (!current.joined || place === null)
          return { hero: t("igwButton.finished"), heroDisabled: true };
        const won = prizeAt(current.data as Tournament, place);
        return {
          hero: won ? formatButtonPrizeTitle(won.title, t) : t("prizes.noWin"),
          ...(won && { heroKind: "prize" as const, heroWon: true }),
          heroDisabled: true,
          prizeCatalog: won?.is_catalog_item,
          top: t("igwButton.finished"),
          topKind: "status" as const,
        };
      }
      if (!(campaignKey(current.campaign) in state.joinedById)) {
        return prize
          ? {
              hero: formatButtonPrizeTitle(prize.title, t),
              heroKind: "prize",
              prizeCatalog: prize.is_catalog_item,
            }
          : { hero: campaignDisplayTitle(current.campaign, t) };
      }
      // Not joined: opting in IS the content — the hero is the big CTA, in
      // the panel's own words, the top prize a badge on its top-right corner
      // (Sebastian, 2026-10-13: "the opt-in should be the main content";
      // "the prize badge on top right, we prioritise prize").
      // Not joined is not joined: the ask stays the hero regardless of the
      // panel prompt's "Not now" memory (Sebastian, 2026-10-13 — a quiet
      // fallback flipped the button on him).
      if (!current.joined) return optInRows(current.campaign, t);
      // Joined and scored: your cell — the rank badge and the score (the wheel
      // around it comes with the tournament pass).
      if (place !== null && score !== null) {
        // The score exactly as the panel prints it (an "x" multiplier for
        // highest-win), and named with the panel's column label.
        const subtype = current.campaign.subtype;
        const highestWin = isHighestWinStyleTournament(subtype);
        const suffix = highestWin ? "x" : "";
        // "pts" after a points score (its own element, plural by the score);
        // a highest-win score carries its "x" in the number instead.
        const unitOf = (n: number) =>
          highestWin ? undefined : t("tournament.pointsUnit", { count: n });
        // The leaderboard wheel (IGB-TW-01): the rows either side of you on
        // the board as the panel shows it (your live score already merged in;
        // neighbours by list position, never by rank arithmetic — ties share
        // a rank). Each neighbour: rank, score (spoken), the prize at that
        // place (drawn).
        // Wire dependency: the `tournament_leaderboards` stream sends the
        // WHOLE board (`LeaderboardEntryMapper` has no cap) — a top-N cap on
        // the backend would silently put the wrong neighbours here.
        const tournament = current.data as Tournament;
        const items = selectLeaderboardItems(
          state,
          campaignKey(current.campaign),
          t
        );
        const me = items.findIndex((item) => item.isPlayer);
        const compactOf = formatCompactScore;
        const cell = (item: TournamentLeaderboardItem | undefined) => {
          if (!item) return null;
          const prize = prizeAt(tournament, item.position);
          return {
            id: item.id,
            rank: item.position,
            ...sideScoreLabel(item.score, subtype, t),
            prize: prize && formatButtonPrizeTitle(prize.title, t),
            catalog: prize?.is_catalog_item,
          };
        };
        const above = me > 0 ? cell(items[me - 1]) : null;
        const below = me >= 0 ? cell(items[me + 1]) : null;
        // One ordering for every number on the wheel (IGB-TW-08, proposed):
        // while the board holds you, your rank is your place in it — the
        // same list the neighbours come from — so two players never share a
        // number; the progress stream's rank only until the board arrives.
        const rank = me >= 0 ? items[me].position : place;
        const myScore = me >= 0 ? items[me].score : score;
        const held = prizeAt(tournament, rank);
        // No prize at your place: how many places to the nearest one that
        // pays (Sebastian, 2026-10-14: "if no prize, show how many positions
        // until the nearest prize position in the player cell").
        const paid = held ? null : nearestPaidAbove(tournament, rank);
        const compact = compactOf(myScore);
        if (ended)
          return {
            hero: `${myScore.toLocaleString()}${suffix}`,
            heroCompact: `${compact}${suffix}`,
            heroUnit: getTournamentScoreColumnLabel(subtype),
            heroScore: myScore,
            scoreUnit: unitOf(myScore),
            heroKind: "standing",
            heroDisabled: true,
            rank,
            ...(held && {
              heroPrize: formatButtonPrizeTitle(held.title, t),
              heroPrizeCatalog: held.is_catalog_item,
            }),
            top: t("igwButton.finished"),
            topKind: "status",
          };
        return {
          hero: `${myScore.toLocaleString()}${suffix}`,
          heroCompact: `${compact}${suffix}`,
          heroUnit: getTournamentScoreColumnLabel(subtype),
          heroScore: myScore,
          scoreUnit: unitOf(myScore),
          heroKind: "standing",
          rank,
          // Your prize in your cell, after the score (IGB-TW-03; Sebastian:
          // "points and prize in the cells, the prize centre-right as text").
          ...(held && {
            heroPrize: formatButtonPrizeTitle(held.title, t),
            heroPrizeCatalog: held.is_catalog_item,
            // The crown: you hold the top prize — the first place range in
            // the client's own table (place 1, or 1–3, or 1–50).
            heroTopPrize: held === topPrize(tournament),
            heroPrizeTier: held.place_from ?? 1,
          }),
          ...(paid !== null && {
            heroHint: t("tournament.placesToPrize", { count: rank - paid }),
            // The wheel's "↑ N" vocabulary (the climb chip), no words to
            // translate: the sentence stays the spoken form.
            heroHintShort: `↑${rank - paid}`,
          }),
          ...(above && {
            aboveId: above.id,
            aboveRank: above.rank,
            aboveScore: above.score,
            aboveUnit: above.unit,
            abovePrize: above.prize,
            abovePrizeCatalog: above.catalog,
          }),
          ...(below && {
            belowId: below.id,
            belowRank: below.rank,
            belowScore: below.score,
            belowUnit: below.unit,
            belowPrize: below.prize,
            belowPrizeCatalog: below.catalog,
          }),
        };
      }
      // Joined, no standing yet: the nudge on the dark chip in the
      // progress token (the finished result's cell and size), the top prize
      // still on its banner above — joining mustn't take the reason to
      // play off the button (👺 Belmont, B5).
      return {
        hero: t("igwButton.tournamentPlayToRank"),
        heroFaded: true,
        heroTone: "progress",
        ...prizeRows,
      };
    },
    // The stream between your old and new place (IGB-TW-07): the board
    // without you, around [lo, hi], and the prize at each of those ranks.
    wheelStream: (current, state, t, lo, hi) => {
      const items = selectLeaderboardItems(
        state,
        campaignKey(current.campaign),
        t
      ).filter((item) => !item.isPlayer);
      if (!items.length) return null;
      // Each row's points, as the side cells draw them (D-139): the one
      // formatter the resting cells use, so the beat hands off to them
      // without a content swap (👺 Belmont, B5).
      const subtype = current.campaign.subtype;
      const others: WheelStreamData["others"] = {};
      for (
        let i = Math.max(0, lo - 2);
        i <= Math.min(items.length - 1, hi);
        i++
      ) {
        others[i] = {
          id: items[i].id ?? `index:${i}`,
          ...sideScoreLabel(items[i].score, subtype, t),
        };
      }
      return { others };
    },
  },
  resolve(state: AppStore, key): CurrentCampaign | null {
    const tournament = selectTournamentById(state, key);
    if (!tournament) return null;
    return {
      type: "tournament",
      campaign: tournament.campaign,
      joined: selectJoined(state, key),
      data: tournament,
    };
  },
  listActive: (state) =>
    state.tournaments
      .filter((t) => isActiveCampaignStatus(t.campaign.status))
      .map((t) => ({ campaign: t.campaign, data: t })),
  listFinished: (state) =>
    state.tournaments
      .filter((t) => isFinishedCampaignStatus(t.campaign.status))
      .map((t) => ({ campaign: t.campaign, data: t })),
  hook: (entry, t) => {
    const prize = topPrize(entry.data as Tournament);
    return prize
      ? {
          label: t("campaign.topPrize", "Top prize"),
          value: formatButtonPrizeTitle(prize.title, t),
          isCatalogItem: prize.is_catalog_item,
        }
      : null;
  },
  homeCard: (entry, layout) => (
    <TournamentHomeCard tournament={entry.data as Tournament} layout={layout} />
  ),
  finishedCard: (entry) => (
    <FinishedTournamentCard tournament={entry.data as Tournament} />
  ),
  detailView,
  dockPanel: (current) =>
    current.type === "tournament" ? (
      <TournamentDockPanel
        tournament={tournamentData(current)}
        joined={current.joined}
      />
    ) : null,
};

registerCampaignType(tournamentModule);
